import { ChevronLeft, ChevronRight, Columns3, TriangleAlert, XCircle } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { StatusText } from '@/components/data/StatusText'
import { Button } from '@/components/ui/button'
import { fmtTimestamp } from '@/lib/http'
import type { TgeAuditResult, TgeOutcome, TgeReportIssue, TgeRunDetail } from '@/lib/tge'

// A whole-grid report holds ~4.5k issues; rendering every row at once stalls the page.
const REPORT_PAGE_SIZE = 100

export const GRIDS = [
  { grid: 27214, table: 4475, label: 'Submitted text/s (27214 → 4475)' },
  { grid: 27807, table: 4418, label: 'Submitted image/s (27807 → 4418)' },
]

export function fmtBytes(n: number): string {
  return n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`
}

export function gridLabel(gridId: number, tableId: number): string {
  return `${gridId} → ${tableId}`
}

/** Tone of a per-issue outcome: the word is always shown, colour only repeats it. */
const OUTCOME_TONE: Record<TgeOutcome, string> = {
  ok: 'text-ok',
  ok_dry: 'text-ok',
  empty_source: 'text-text-2',
  size_exceeded: 'text-danger',
  skipped_missing_issue: 'text-warn',
  skipped_missing_dc_key: 'text-warn',
  skipped_not_on_dc: 'text-warn',
  no_mapped_values: 'text-warn',
  verify_mismatch: 'text-danger',
  error: 'text-danger',
}

export function OutcomeText({ outcome }: { outcome: TgeOutcome }) {
  return <span className={`font-mono text-[12px] font-medium ${OUTCOME_TONE[outcome] ?? 'text-text-2'}`}>{outcome}</span>
}

/** Run status word: ok / partial / error / running. */
export function RunStatus({ status }: { status: string }) {
  const word = status === 'ok' ? 'success' : status === 'error' ? 'failed' : status
  return <StatusText status={word} className="text-[12px]" />
}

/** Small-caps heading of a block inside the center column. */
export function BlockTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3">
      <h2 className="text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">{children}</h2>
      {aside}
    </div>
  )
}

/** Big mono counter with a caption: "41 rows total". */
function Count({ value, label, tone = 'text-foreground' }: { value: ReactNode; label: string; tone?: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={`font-mono text-[18px] leading-6 ${tone}`}>{value}</span>
      <span className="text-[12.5px] text-text-2">{label}</span>
    </span>
  )
}

/** Collapsible list of plain strings (transform warnings). */
export function CollapsibleList({ title, items, defaultOpen = false }: { title: string; items: string[]; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 py-1 text-left text-[12.5px] text-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight className={`size-3.5 shrink-0 text-text-3 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />
        <span>{title}</span>
        <span className={`font-mono text-[12px] ${items.length > 0 ? 'text-warn' : 'text-text-3'}`}>{items.length}</span>
      </button>
      {open && (items.length === 0
        ? <p className="py-1 pl-5 text-[12px] text-text-3">None.</p>
        : (
          <ul className="max-h-72 overflow-y-auto pl-5">
            {items.map((w, i) => (
              <li key={i} className="break-words border-b border-line-row py-1 font-mono text-[11.5px] text-text-2">{w}</li>
            ))}
          </ul>
        ))}
    </div>
  )
}

// ---- audit result -------------------------------------------------------------------

/** Audit summary; `children` is the column-map editor (it lives with the page's state). */
export function AuditResult({ audit, children }: { audit: TgeAuditResult; children: ReactNode }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <Count value={audit.issues} label="issues" />
          <Count value={audit.rows_total} label="rows total" />
          <Count
            value={audit.oversize.length}
            label="oversize (>31KB)"
            tone={audit.oversize.length > 0 ? 'text-danger' : 'text-ok'}
          />
        </div>
        <p className="mt-1 font-mono text-[11px] text-text-3">
          grid {audit.grid_id} → table {audit.table_id} · fields config {audit.fields_id_used} · TGE rowcount{' '}
          {audit.rowcount ?? '—'}
        </p>
      </div>

      {audit.oversize.length > 0 && (
        <section>
          <BlockTitle>
            <span className="inline-flex items-center gap-1.5 text-danger">
              <TriangleAlert className="size-3.5" aria-hidden />
              oversize — migrate will skip these
            </span>
          </BlockTitle>
          <ul className="divide-y divide-line-row border-y border-line-row">
            {audit.oversize.map((o) => (
              <li key={o.issue_id} className="flex gap-3 py-1 font-mono text-[12px]">
                <span className="text-foreground">{o.issue_key ?? `#${o.issue_id}`}</span>
                <span className="text-text-2">{o.rows} rows · {fmtBytes(o.size_estimate)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {audit.missing_dc_keys.length > 0 && (
        <p className="flex items-start gap-1.5 font-mono text-[12px] text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            {audit.missing_dc_keys.length} issue id{audit.missing_dc_keys.length === 1 ? '' : 's'} deleted on DC
            (no key — migrate will skip): {audit.missing_dc_keys.slice(0, 20).join(', ')}
            {audit.missing_dc_keys.length > 20 ? ', …' : ''}
          </span>
        </p>
      )}

      {children}
    </div>
  )
}

// ---- run report ---------------------------------------------------------------------

const ISSUE_COLUMNS: Column<TgeReportIssue>[] = [
  { key: 'issue', header: 'Issue', mono: true, width: '16%', render: (i) => i.issue_key ?? `#${i.issue_id}`, title: (i) => i.issue_key ?? `#${i.issue_id}` },
  { key: 'rows', header: 'TGE rows', mono: true, width: '96px', align: 'right' },
  { key: 'verified', header: 'STAGIL rows', mono: true, width: '110px', align: 'right', render: (i) => i.verified_rows ?? '—' },
  { key: 'size', header: 'Size est.', mono: true, width: '100px', align: 'right', render: (i) => (i.size_estimate !== undefined ? fmtBytes(i.size_estimate) : '—') },
  { key: 'outcome', header: 'Outcome', width: '190px', render: (i) => <OutcomeText outcome={i.outcome} />, title: (i) => i.outcome },
  { key: 'error', header: 'Reason', render: (i) => (i.error ? <span className="font-mono text-[12px] text-danger">{i.error}</span> : ''), title: (i) => i.error },
]

/** Outcome chips: a radio group that filters the per-issue table. */
function OutcomeChips({ counts, value, onChange }: {
  counts: [string, number][]
  value: string
  onChange: (v: string) => void
}) {
  const all = counts.reduce((n, [, c]) => n + c, 0)
  const items: [string, number][] = [['all', all], ...counts]
  return (
    <div role="group" aria-label="Filter by outcome" className="flex flex-wrap gap-1.5">
      {items.map(([k, n]) => {
        const on = value === k
        return (
          <button
            key={k}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(k)}
            className={[
              'rounded-full border px-2.5 py-0.5 text-[11.5px] transition-colors',
              on ? 'border-primary bg-surface-active text-foreground' : 'border-line-strong text-text-2 hover:text-foreground',
            ].join(' ')}
          >
            {k} <span className="font-mono">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

/** `onMapColumns`: audit this run's grid and open its column map — offered when the run
 * stopped because no column was mapped (runs #20, #21). */
export function RunReport({ run, onMapColumns }: { run: TgeRunDetail; onMapColumns?: () => void }) {
  const [filter, setFilter] = useState<string>('all')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)

  // A different run starts from all outcomes and the first page.
  useEffect(() => { setFilter('all'); setSelected(null) }, [run.id])
  useEffect(() => setPage(0), [run.id, filter])

  const report = run.report
  const issues: TgeReportIssue[] = report !== null && 'issues' in report ? report.issues : []
  const counts = new Map<string, number>()
  for (const i of issues) counts.set(i.outcome, (counts.get(i.outcome) ?? 0) + 1)
  const visible = filter === 'all' ? issues : issues.filter((i) => i.outcome === filter)
  const pageCount = Math.max(1, Math.ceil(visible.length / REPORT_PAGE_SIZE))
  // Clamp for the render between a filter change and the reset effect.
  const safePage = Math.min(page, pageCount - 1)
  const pageStart = safePage * REPORT_PAGE_SIZE
  const pageItems = visible.slice(pageStart, pageStart + REPORT_PAGE_SIZE)
  const issueId = (i: TgeReportIssue) => String(i.issue_id ?? i.issue_key ?? '')
  // Auto-pick the only failed issue so its full reason is visible without a click.
  const errored = issues.filter((i) => i.error)
  const selectedIssue =
    issues.find((i) => issueId(i) === selected) ?? (errored.length === 1 ? errored[0] : undefined)
  const t = run.totals

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-3 px-4 pt-3">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] text-text-3">
          <span className="font-sans text-[13px] font-semibold text-foreground">Run #{run.id}</span>
          <span aria-hidden>·</span>
          {gridLabel(run.grid_id, run.table_id)}
          <span aria-hidden>·</span>
          {run.dry_run ? 'dry run' : 'live'}
          <span aria-hidden>·</span>
          <RunStatus status={run.status} />
          <span aria-hidden>·</span>
          started {fmtTimestamp(run.started_at)}
          {run.finished_at && <> · finished {fmtTimestamp(run.finished_at)}</>}
        </p>

        {report !== null && 'error' in report && (
          <div className="space-y-2">
            <p className="flex items-start gap-2 break-words font-mono text-[12px] text-danger">
              <XCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              {report.error}
            </p>
            {report.needs_column_map && onMapColumns && (
              <div className="flex flex-wrap items-center gap-3 pl-6">
                <Button size="sm" onClick={onMapColumns}>
                  <Columns3 aria-hidden /> Map the columns
                </Button>
                <span className="text-[12px] text-text-2">
                  Runs the audit of grid {run.grid_id} and opens its column map. The map you pick is
                  saved — the next run uses it.
                </span>
              </div>
            )}
          </div>
        )}

        {t && (
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <Count value={t.issues} label="issues" />
            <Count value={t.rows} label="rows" />
            <Count value={t.warnings} label="warnings" tone={t.warnings > 0 ? 'text-warn' : 'text-foreground'} />
            {(Object.entries(t.outcomes) as [TgeOutcome, number][]).map(([o, n]) => (
              <span key={o} className="inline-flex items-baseline gap-1">
                <span className="font-mono text-[12.5px] text-foreground">{n}</span>
                <OutcomeText outcome={o} />
              </span>
            ))}
          </div>
        )}

        {report !== null && 'column_map' in report && (
          <p className="flex flex-wrap items-baseline gap-x-2 font-mono text-[11.5px] text-text-3">
            <span>
              columns: {Object.keys(report.column_map).length} migrated
            </span>
            {report.unmatched_columns.length > 0 && (
              <span className="inline-flex items-center gap-1 text-warn">
                <TriangleAlert className="size-3 shrink-0" aria-hidden />
                not migrated: {report.unmatched_columns.join(', ')}
              </span>
            )}
          </p>
        )}

        {report !== null && 'warnings' in report && (
          <CollapsibleList title="Transform warnings" items={report.warnings} />
        )}
      </div>

      {issues.length > 0 && (
        <div className="mt-3 flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-3 px-4 pb-2">
            <h2 className="text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">per-issue outcomes</h2>
            <OutcomeChips counts={[...counts.entries()]} value={filter} onChange={setFilter} />
          </div>
          <div className="flex min-h-0 flex-1 flex-col border-t border-line">
            <DataTable
              label="Per-issue outcomes"
              rows={pageItems}
              columns={ISSUE_COLUMNS}
              rowId={issueId}
              selectedId={selectedIssue ? issueId(selectedIssue) : null}
              onSelect={setSelected}
              empty="No issues with this outcome."
            />
          </div>
          {selectedIssue?.error && (
            <section
              aria-label="Selected issue reason"
              className="shrink-0 border-t border-line bg-surface px-4 py-2.5"
            >
              <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-[0.07em] text-text-3">
                <span>Reason</span>
                <span className="font-mono normal-case tracking-normal text-text-2">
                  {selectedIssue.issue_key ?? `#${selectedIssue.issue_id}`}
                </span>
              </div>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-danger">
                {selectedIssue.error}
              </pre>
            </section>
          )}
          {visible.length > REPORT_PAGE_SIZE && (
            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-line px-4 py-1.5 font-mono text-[12px] text-text-2">
              <span aria-live="polite">
                showing {pageStart + 1}–{pageStart + pageItems.length} of {visible.length}
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={safePage === 0}
                  onClick={() => setPage(safePage - 1)}
                  className="inline-flex items-center gap-1 rounded-md border border-line-strong bg-surface-raised px-2 py-0.5 text-foreground disabled:opacity-60"
                >
                  <ChevronLeft className="size-3.5" aria-hidden /> Prev
                </button>
                <span>{safePage + 1}/{pageCount}</span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                  className="inline-flex items-center gap-1 rounded-md border border-line-strong bg-surface-raised px-2 py-0.5 text-foreground disabled:opacity-60"
                >
                  Next <ChevronRight className="size-3.5" aria-hidden />
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
